import { TILE } from '../../shared/geo.js';
import { MESH_STEP } from '../../shared/heightmap.js';
import { noise2 } from '../../shared/rng.js';

// The ground of a tile as plain numbers: its painted picture and its mesh vertices. Nothing here
// touches the renderer or the page, so it runs the same on the main thread and in a worker.

export const GROUND_PX = 256; // pixels across a tile's ground picture
const PX = GROUND_PX / (TILE * 10); // tile data is in decimetres

// Land-use fills painted over the base ground, by the kind index in the map data.
const LAND_FILL = [
  'rgba(44,92,48,0.88)', // 0 forest
  'rgba(86,118,62,0.6)', // 1 scrub
  'rgba(156,150,84,0.5)', // 2 farmland
  'rgba(118,170,82,0.55)', // 3 grass
  'rgba(170,116,84,0.34)', // 4 residential
  'rgba(152,128,112,0.5)', // 5 commercial
  'rgba(140,134,128,0.5)', // 6 industrial
  'rgba(104,128,96,0.55)', // 7 cemetery
  'rgba(92,158,72,0.9)', // 8 sports pitch
  'rgba(178,150,110,0.36)', // 9 school grounds
  'rgba(92,92,96,0.92)', // 10 airport apron
];

// Colour of bare ground from height, steepness and a little noise: [r, g, b].
export function groundColor(x, z, height, slope, out) {
  const n = noise2(x / 90, z / 90, 11) * 0.6 + noise2(x / 23, z / 23, 12) * 0.4;
  // grass, yellower high up
  const high = Math.max(0, Math.min(1, (height - 1700) / 700));
  let r = 84 + n * 30 + high * 50;
  let g = 146 + n * 28 + high * 6;
  let b = 66 + n * 16 + high * 2;
  // steep ground shows red earth and rock
  const steep = Math.max(0, Math.min(1, (slope - 0.38) / 0.4));
  r += (158 - r) * steep;
  g += (112 - g) * steep;
  b += (86 - b) * steep;
  out[0] = r;
  out[1] = g;
  out[2] = b;
}

function path(g, p) {
  g.beginPath();
  g.moveTo(p[0] * PX, p[1] * PX);
  for (let i = 2; i < p.length; i += 2) g.lineTo(p[i] * PX, p[i + 1] * PX);
}

// Paints the ground picture for one tile onto a GROUND_PX-square canvas (a page canvas or an
// OffscreenCanvas): base colours, land use, bare earth around buildings and along roads, and dark
// beds under water. Returns the canvas.
export function paintGround(c, data, tx, tz, heightmap) {
  const g = c.getContext('2d');
  const img = g.createImageData(GROUND_PX, GROUND_PX);
  const m = TILE / GROUND_PX;
  const col = [0, 0, 0];
  for (let j = 0; j < GROUND_PX; j += 2) {
    for (let i = 0; i < GROUND_PX; i += 2) {
      const x = tx * TILE + (i + 1) * m;
      const z = tz * TILE + (j + 1) * m;
      const h = heightmap.gridHeight(x, z);
      const sx = (heightmap.gridHeight(x + 12, z) - heightmap.gridHeight(x - 12, z)) / 24;
      const sz = (heightmap.gridHeight(x, z + 12) - heightmap.gridHeight(x, z - 12)) / 24;
      groundColor(x, z, h, Math.hypot(sx, sz), col);
      for (let dj = 0; dj < 2; dj++) {
        for (let di = 0; di < 2; di++) {
          const k = ((j + dj) * GROUND_PX + i + di) * 4;
          img.data[k] = col[0];
          img.data[k + 1] = col[1];
          img.data[k + 2] = col[2];
          img.data[k + 3] = 255;
        }
      }
    }
  }
  g.putImageData(img, 0, 0);
  if (!data) return c;

  for (const l of data.l) {
    path(g, l.p);
    g.fillStyle = LAND_FILL[l.k] || LAND_FILL[3];
    g.fill();
    if (l.k === 2) {
      // farm plots: ridges across the field
      g.save();
      g.clip();
      g.strokeStyle = 'rgba(92,110,56,0.45)';
      g.lineWidth = 1;
      for (let y = 0; y < GROUND_PX; y += 4) {
        g.beginPath();
        g.moveTo(0, y + 0.5);
        g.lineTo(GROUND_PX, y + 0.5);
        g.stroke();
      }
      g.restore();
    }
  }
  g.lineJoin = 'round';
  g.lineCap = 'round';
  // trodden earth around every building
  g.fillStyle = 'rgba(166,122,96,0.5)';
  g.strokeStyle = 'rgba(166,122,96,0.32)';
  g.lineWidth = 7;
  for (const b of data.b) {
    path(g, b.p);
    g.closePath();
    g.stroke();
    g.fill();
  }
  for (const r of data.r) {
    path(g, r.p);
    g.strokeStyle = r.s === 1 ? 'rgba(160,116,92,0.42)' : 'rgba(128,112,100,0.45)';
    g.lineWidth = (r.w + 3.5) * 10 * PX;
    g.stroke();
  }
  g.strokeStyle = 'rgba(52,84,74,0.8)';
  for (const w of data.w) {
    path(g, w.p);
    g.lineWidth = (w.w + 3) * 10 * PX;
    g.stroke();
  }
  g.fillStyle = 'rgba(46,78,84,0.9)';
  for (const a of data.a) {
    path(g, a.p);
    g.fill();
  }
  return c;
}

// The vertices of a square of ground `size` metres across whose corner is at world (ox, oz):
// positions local to that corner with real heights, normals, uvs spanning the rect
// [u0, v0] at the corner to [u1, v1] at the far corner, and the triangle index. Each square
// is split along the a-d diagonal, matching Heightmap.heightAt.
export function terrainArrays(ox, oz, size, heightmap, u0 = 0, v0 = 1, u1 = 1, v1 = 0) {
  const n = size / MESH_STEP;
  const side = n + 1;
  const pos = new Float32Array(side * side * 3);
  const nor = new Float32Array(side * side * 3);
  const uv = new Float32Array(side * side * 2);
  for (let j = 0; j < side; j++) {
    for (let i = 0; i < side; i++) {
      const k = j * side + i;
      const x = i * MESH_STEP;
      const z = j * MESH_STEP;
      pos[k * 3] = x;
      pos[k * 3 + 1] = heightmap.gridHeight(ox + x, oz + z);
      pos[k * 3 + 2] = z;
      const dx = heightmap.gridHeight(ox + x + MESH_STEP, oz + z) - heightmap.gridHeight(ox + x - MESH_STEP, oz + z);
      const dz = heightmap.gridHeight(ox + x, oz + z + MESH_STEP) - heightmap.gridHeight(ox + x, oz + z - MESH_STEP);
      const nx = -dx / (2 * MESH_STEP);
      const nz = -dz / (2 * MESH_STEP);
      const len = Math.hypot(nx, 1, nz);
      nor[k * 3] = nx / len;
      nor[k * 3 + 1] = 1 / len;
      nor[k * 3 + 2] = nz / len;
      uv[k * 2] = u0 + ((u1 - u0) * i) / n;
      uv[k * 2 + 1] = v0 + ((v1 - v0) * j) / n;
    }
  }
  const index = new Uint16Array(n * n * 6);
  let q = 0;
  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      const a = j * side + i;
      const b = a + 1;
      const c = a + side;
      const d = c + 1;
      index[q++] = a;
      index[q++] = c;
      index[q++] = d;
      index[q++] = a;
      index[q++] = d;
      index[q++] = b;
    }
  }
  return { pos, nor, uv, index };
}
